import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    Paper,
    TextField,
    IconButton,
    Button,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Chip,
    CircularProgress,
    Tooltip,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    useTheme,
    alpha,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import ChatMessage from '../components/ChatMessage';
import {
    listChatSessions,
    getChatSessionDetail,
    deleteChatSession,
    streamAdvisorChat,
} from '../services/advisorChatApi';
import { AdvisorChatRole, AdvisorChatMessageStatus } from '../constants/enums';


const STARTER_PROMPTS = [
    { text: 'What were my highest expenses this month?' },
    { text: 'What recurring subscriptions am I paying for?' },
    { text: 'How does my actual spending compare to my 2026 budget?' },
    { text: 'Show my investment portfolio breakdown.' },
];

const Advisor = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { id: ledgerId } = useParams();
    const { currentUser, token, api } = useAuth();
    const { showToast } = useToast();

    const [sessions, setSessions] = useState([]);
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [inputPrompt, setInputPrompt] = useState('');
    const [isLoadingSessions, setIsLoadingSessions] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const [streamingDelta, setStreamingDelta] = useState('');
    const [activeTool, setActiveTool] = useState(null);
    const [streamToolsUsed, setStreamToolsUsed] = useState([]);

    // Delete confirmation dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [sessionToDelete, setSessionToDelete] = useState(null);
    const [isDeletingSession, setIsDeletingSession] = useState(false);

    const abortControllerRef = useRef(null);
    const messagesEndRef = useRef(null);

    const isAdmin =
        currentUser?.service_tier === 'ADMIN' ||
        currentUser?.is_super_admin === true;

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, streamingDelta, activeTool]);

    // Fetch sessions list on mount or ledger change
    useEffect(() => {
        if (!ledgerId || !isAdmin) return;
        let isMounted = true;
        setIsLoadingSessions(true);
        listChatSessions(api, ledgerId)
            .then((data) => {
                if (!isMounted) return;
                const sessionList = data || [];
                setSessions(sessionList);
                if (sessionList.length > 0) {
                    setCurrentSessionId(sessionList[0].id);
                } else {
                    setCurrentSessionId(null);
                }
            })
            .catch((err) => {
                console.error('Failed to load advisor chat sessions:', err);
            })
            .finally(() => {
                if (isMounted) {
                    setIsLoadingSessions(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [ledgerId, isAdmin, api]);

    // Load message history when currentSessionId changes
    useEffect(() => {
        if (!ledgerId || !isAdmin) return;
        if (!currentSessionId) {
            setMessages([]);
            return;
        }

        const loadMessages = async () => {
            try {
                const detail = await getChatSessionDetail(api, ledgerId, currentSessionId);
                setMessages(detail?.messages || []);
            } catch (err) {
                console.error('Failed to load session messages:', err);
            }
        };

        loadMessages();
    }, [ledgerId, currentSessionId, isAdmin, api]);

    const handleNewChat = () => {
        if (isStreaming) {
            handleStopStream();
        }
        setCurrentSessionId(null);
        setMessages([]);
        setStreamingDelta('');
        setActiveTool(null);
        setInputPrompt('');
    };

    const handleSelectSession = (sId) => {
        if (isStreaming) {
            handleStopStream();
        }
        setCurrentSessionId(sId);
        setStreamingDelta('');
        setActiveTool(null);
    };

    const handleDeleteClick = (s, e) => {
        e.stopPropagation();
        setSessionToDelete(s);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!sessionToDelete) return;
        setIsDeletingSession(true);
        try {
            await deleteChatSession(api, ledgerId, sessionToDelete.id);
            setSessions(prev => prev.filter(s => s.id !== sessionToDelete.id));
            if (currentSessionId === sessionToDelete.id) {
                handleNewChat();
            }
            showToast(t('Conversation deleted successfully.'), 'success');
            setDeleteDialogOpen(false);
            setSessionToDelete(null);
        } catch (err) {
            console.error('Failed to delete session:', err);
            showToast(t('Failed to delete conversation.'), 'error');
        } finally {
            setIsDeletingSession(false);
        }
    };

    const handleStopStream = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setIsStreaming(false);
        setActiveTool(null);
    };

    const handleSendMessage = async (textToSend) => {
        const promptText = (textToSend || inputPrompt).trim();
        if (!promptText || isStreaming) return;

        setInputPrompt('');
        setIsStreaming(true);
        setStreamingDelta('');
        setActiveTool(null);
        setStreamToolsUsed([]);

        // Optimistically append user message to local state
        const userMsg = {
            id: Date.now(),
            role: AdvisorChatRole.USER,
            content: promptText,
            status: AdvisorChatMessageStatus.COMPLETE,
            created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev, userMsg]);

        abortControllerRef.current = new AbortController();

        let accumulatedText = '';
        let toolsCollected = [];

        await streamAdvisorChat({
            ledgerId,
            token,
            prompt: promptText,
            sessionId: currentSessionId,
            signal: abortControllerRef.current.signal,
            onSession: (sessionData) => {
                if (sessionData?.session_id) {
                    setCurrentSessionId(sessionData.session_id);
                    // Update or prepend to sessions list
                    setSessions(prev => {
                        const exists = prev.find(s => s.id === sessionData.session_id);
                        if (exists) {
                            return prev.map(s =>
                                s.id === sessionData.session_id
                                    ? { ...s, title: sessionData.title || s.title, updated_at: new Date().toISOString() }
                                    : s
                            );
                        }
                        return [
                            {
                                id: sessionData.session_id,
                                title: sessionData.title || 'New Conversation',
                                message_count: 1,
                                updated_at: new Date().toISOString(),
                            },
                            ...prev,
                        ];
                    });
                }
            },
            onStatus: (statusData) => {
                if (statusData?.tool) {
                    setActiveTool(statusData.tool);
                    if (!toolsCollected.includes(statusData.tool)) {
                        toolsCollected.push(statusData.tool);
                        setStreamToolsUsed([...toolsCollected]);
                    }
                }
            },
            onDelta: (chunk) => {
                accumulatedText += chunk;
                setStreamingDelta(accumulatedText);
                setActiveTool(null); // Clear tool indicator once text flows
            },
            onDone: (doneData) => {
                setIsStreaming(false);
                setActiveTool(null);
                setStreamingDelta('');

                const assistantMsg = {
                    id: doneData?.message_id || Date.now() + 1,
                    role: AdvisorChatRole.ASSISTANT,
                    content: accumulatedText,
                    status: AdvisorChatMessageStatus.COMPLETE,
                    tools_used: doneData?.tools_used || toolsCollected,
                    created_at: new Date().toISOString(),
                };
                setMessages(prev => [...prev, assistantMsg]);
            },
            onError: (err) => {
                setIsStreaming(false);
                setActiveTool(null);
                setStreamingDelta('');

                const errorMsg = {
                    id: Date.now() + 2,
                    role: AdvisorChatRole.ASSISTANT,
                    content: `⚠️ **Error:** ${err || 'An unexpected issue occurred while analyzing data.'}`,
                    status: AdvisorChatMessageStatus.ERROR,
                    created_at: new Date().toISOString(),
                };
                setMessages(prev => [...prev, errorMsg]);
            },
        });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Non-Admin Locked Screen
    // ─────────────────────────────────────────────────────────────────────────
    if (!isAdmin) {
        return (
            <Container maxWidth="md" sx={{ mt: 8, mb: 8, textAlign: 'center' }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 4, sm: 6 },
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                    }}
                >
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            mx: 'auto',
                            mb: 2.5,
                            borderRadius: '50%',
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <LockOutlinedIcon sx={{ fontSize: 32 }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1.5, fontFamily: '"Manrope", sans-serif' }}>
                        {t('AI Financial Advisor is an Admin Feature')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto', mb: 3.5, lineHeight: 1.7 }}>
                        {t('The interactive conversational advisor with live ledger analytics, budgeting breakdowns, and portfolio reasoning is exclusively available to Admin tier users.')}
                    </Typography>
                    <Chip
                        label={t('Current Tier: {{tier}}', { tier: currentUser?.service_tier || 'FREE' })}
                        variant="outlined"
                        sx={{ fontWeight: 600, py: 1.5, px: 1, borderColor: 'divider' }}
                    />
                </Paper>
            </Container>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Main Admin View
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 6, height: 'calc(100vh - 140px)', display: 'flex', flexDirection: 'column' }}>
            {/* Page Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                        {t('AI Financial Advisor')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {t('Real-time ledger intelligence and personalized financial guidance powered by Gemini.')}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Chip
                        icon={<AutoAwesomeIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
                        label={t('Gemini 3.8 Flash')}
                        variant="outlined"
                        size="small"
                        sx={{ fontWeight: 600, borderColor: 'divider', color: 'text.secondary' }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleNewChat}
                        sx={{ fontWeight: 600, borderRadius: 1.5, textTransform: 'none' }}
                    >
                        {t('New Conversation')}
                    </Button>
                </Box>
            </Box>

            {/* Interactive Chat Workspace */}
            <Paper
                elevation={0}
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                    minHeight: 0,
                }}
            >
                {/* Conversations Drawer / Sidebar */}
                <Box
                    sx={{
                        width: { xs: 72, sm: 260, md: 280 },
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        bgcolor: alpha(theme.palette.secondary.main, 0.02),
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                color: 'text.secondary',
                                display: { xs: 'none', sm: 'block' },
                            }}
                        >
                            {t('Conversations')}
                        </Typography>
                        {sessions.length > 0 && (
                            <Chip
                                label={sessions.length}
                                size="small"
                                sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    bgcolor: alpha(theme.palette.secondary.main, 0.08),
                                    color: 'text.secondary',
                                    display: { xs: 'none', sm: 'inline-flex' },
                                }}
                            />
                        )}
                    </Box>

                    <Divider />

                    {/* Sessions List */}
                    <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
                        {isLoadingSessions ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress size={24} color="primary" />
                            </Box>
                        ) : sessions.length === 0 ? (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('No past conversations.')}
                                </Typography>
                            </Box>
                        ) : (
                            <List sx={{ p: 0 }}>
                                {sessions.map((s) => {
                                    const isSelected = s.id === currentSessionId;
                                    return (
                                        <ListItem
                                            key={s.id}
                                            disablePadding
                                            sx={{ mb: 0.5 }}
                                            secondaryAction={
                                                <IconButton
                                                    edge="end"
                                                    size="small"
                                                    onClick={(e) => handleDeleteClick(s, e)}
                                                    sx={{
                                                        opacity: isSelected ? 0.8 : 0.25,
                                                        '&:hover': { opacity: 1, color: 'error.main' },
                                                    }}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            }
                                        >
                                            <ListItemButton
                                                selected={isSelected}
                                                onClick={() => handleSelectSession(s.id)}
                                                sx={{
                                                    borderRadius: 1.5,
                                                    py: 1,
                                                    px: 1.5,
                                                    pr: 4,
                                                    '&.Mui-selected': {
                                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                                        color: 'primary.main',
                                                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.12) },
                                                    },
                                                    '&:hover': {
                                                        bgcolor: alpha(theme.palette.text.primary, 0.04),
                                                    },
                                                }}
                                            >
                                                <ChatBubbleOutlineIcon
                                                    sx={{
                                                        fontSize: 18,
                                                        mr: 1.5,
                                                        color: isSelected ? 'primary.main' : 'text.secondary',
                                                        opacity: isSelected ? 1 : 0.7,
                                                    }}
                                                />
                                                <ListItemText
                                                    primary={s.title}
                                                    primaryTypographyProps={{
                                                        variant: 'body2',
                                                        fontWeight: isSelected ? 700 : 500,
                                                        noWrap: true,
                                                        color: isSelected ? 'primary.main' : 'text.primary',
                                                    }}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        )}
                    </Box>
                </Box>

                {/* Main Chat Thread Area */}
                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.default', minWidth: 0 }}>
                    {/* Messages Scroll Area */}
                    <Box sx={{ flexGrow: 1, overflowY: 'auto', p: { xs: 2, sm: 3 } }}>
                        {messages.length === 0 && !isStreaming ? (
                            /* Starter Prompts & Empty State */
                            <Box sx={{ maxWidth: 760, mx: 'auto', textAlign: 'center', py: { xs: 3, sm: 6 } }}>
                                <Box
                                    sx={{
                                        width: 52,
                                        height: 52,
                                        mx: 'auto',
                                        mb: 2,
                                        borderRadius: 2,
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                        color: 'primary.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <AutoAwesomeIcon sx={{ fontSize: 28 }} />
                                </Box>
                                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, fontFamily: '"Manrope", sans-serif' }}>
                                    {t('How can I help with your finances today?')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 4, maxWidth: 560, mx: 'auto', lineHeight: 1.6 }}>
                                    {t('Ask about your budget, transactions, recurring bills, credit card rewards, or investment portfolio.')}
                                </Typography>

                                {/* Starter Cards Grid */}
                                <Grid container spacing={1.5} sx={{ textAlign: 'left' }}>
                                    {STARTER_PROMPTS.map((starter, idx) => (
                                        <Grid item xs={12} sm={6} key={idx}>
                                            <Paper
                                                elevation={0}
                                                onClick={() => handleSendMessage(starter.text)}
                                                sx={{
                                                    p: 2,
                                                    height: '100%',
                                                    borderRadius: 2,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'space-between',
                                                    bgcolor: 'background.paper',
                                                    '&:hover': {
                                                        borderColor: 'primary.main',
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: '0 8px 24px rgba(28, 28, 24, 0.06)',
                                                    },
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1.5 }}>
                                                    {starter.text}
                                                </Typography>
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                    <SendIcon sx={{ fontSize: 16, color: 'primary.main', opacity: 0.7 }} />
                                                </Box>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        ) : (
                            /* Message Stream Thread */
                            <Box sx={{ maxWidth: 840, mx: 'auto' }}>
                                {messages.map((msg) => (
                                    <ChatMessage
                                        key={msg.id}
                                        role={msg.role}
                                        content={msg.content}
                                        toolsUsed={msg.tools_used}
                                    />
                                ))}

                                {/* Active Streaming Chunk Message */}
                                {isStreaming && (
                                    <ChatMessage
                                        role={AdvisorChatRole.ASSISTANT}
                                        content={streamingDelta || (activeTool ? '' : '...')}
                                        activeTool={activeTool}
                                        toolsUsed={streamToolsUsed}
                                        isStreaming={true}
                                    />
                                )}

                                <div ref={messagesEndRef} />
                            </Box>
                        )}
                    </Box>

                    {/* Bottom Input Area */}
                    <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                        <Box sx={{ maxWidth: 840, mx: 'auto' }}>
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={1}
                                    maxRows={4}
                                    variant="outlined"
                                    placeholder={t('Ask anything about this ledger... (e.g. "What were my top expenses last month?")')}
                                    value={inputPrompt}
                                    onChange={(e) => setInputPrompt(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    disabled={isStreaming}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            bgcolor: alpha(theme.palette.secondary.main, 0.03),
                                            '& fieldset': {
                                                borderColor: 'divider',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: alpha(theme.palette.primary.main, 0.5),
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'primary.main',
                                            },
                                        },
                                    }}
                                />

                                {isStreaming ? (
                                    <Tooltip title={t('Stop generating')}>
                                        <IconButton
                                            onClick={handleStopStream}
                                            sx={{
                                                bgcolor: 'error.main',
                                                color: '#ffffff',
                                                p: 1.25,
                                                borderRadius: 2,
                                                '&:hover': { bgcolor: 'error.dark' },
                                            }}
                                        >
                                            <StopIcon />
                                        </IconButton>
                                    </Tooltip>
                                ) : (
                                    <Tooltip title={t('Send message')}>
                                        <span>
                                            <IconButton
                                                onClick={() => handleSendMessage()}
                                                disabled={!inputPrompt.trim()}
                                                sx={{
                                                    bgcolor: 'primary.main',
                                                    color: '#ffffff',
                                                    p: 1.25,
                                                    borderRadius: 2,
                                                    '&:hover': { bgcolor: 'primary.dark' },
                                                    '&.Mui-disabled': {
                                                        bgcolor: 'action.disabledBackground',
                                                        color: 'action.disabled',
                                                    },
                                                }}
                                            >
                                                <SendIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.8, textAlign: 'center', fontSize: '0.75rem' }}>
                                {t('Press Enter to send, Shift + Enter for new line. Advisor answers are grounded strictly in your ledger data.')}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Paper>

            {/* Delete Session Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={isDeletingSession ? undefined : () => setDeleteDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 2, p: 1 }
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontFamily: '"Manrope", sans-serif', color: 'error.main' }}>
                    {t('Delete Conversation?')}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        {t('Are you sure you want to delete')} <strong>{sessionToDelete?.title}</strong>?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('This conversation history cannot be recovered.')}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                        disabled={isDeletingSession}
                        color="inherit"
                        sx={{ fontWeight: 600, textTransform: 'none' }}
                    >
                        {t('Cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        disabled={isDeletingSession}
                        color="error"
                        variant="contained"
                        startIcon={isDeletingSession ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
                        sx={{ fontWeight: 700, textTransform: 'none' }}
                        autoFocus
                    >
                        {isDeletingSession ? t('Deleting...') : t('Delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Advisor;
